import {
  getPaymentInstruments,
  InstrumentStatus,
  type PaymentInstrument,
  type PaymentModuleId,
} from '@foundation/apis/payments';

import { StartupAuthContext } from '@/services/base/Startupable';
import { getWindowParam } from '@/utils/url';

import { getPaymentIntentInstruments } from './PaymentInstrumentsApi';
import {
  determineInstrumentPreSelection,
  withMandateReferenceAsId,
} from '../utils/InstrumentUtils';

export class PaymentInstrumentsStore {
  public readonly name = 'PaymentInstrumentsStore';

  private _instruments: ReadonlyArray<PaymentInstrument> = [];
  private _preSelectedInstrument: PaymentInstrument | undefined;
  private _rawPreSelectedPaymentMethod: PaymentModuleId | undefined;

  //this prop represents a specified in the url instrument that will be used for payment
  private _externalPreselectedInstrument: PaymentInstrument | undefined;
  private _authContext: StartupAuthContext | undefined;

  constructor(
    rawPreSelectedPaymentMethod: PaymentModuleId | undefined,
    authContext?: StartupAuthContext,
  ) {
    this._rawPreSelectedPaymentMethod = rawPreSelectedPaymentMethod;
    this._authContext = authContext;
  }

  public async init(paymentIntentId: string): Promise<void> {
    const instrumentIdSearchParam = getWindowParam('instrumentId');
    const instrumentId = instrumentIdSearchParam ?? undefined;

    if (instrumentId !== undefined) {
      const customerNumber = this._authContext?.customerNumber;
      const accessToken = this._authContext?.accessToken ?? '';

      if (customerNumber === undefined) {
        throw new Error('Customer number is not set');
      }

      /*Since external selected instrument (from url) could be any user's instrument (not necessairly in the context of the payment intent - e.g. it could have been added in other legal entity)
      we fetch all user's instruments as we do in the new /manage page*/
      this._instruments = (
        await getPaymentInstruments({
          tpId: customerNumber,
          bearerToken: accessToken,
        })
      ).map(withMandateReferenceAsId);

      //even though we need only id of the instrument better to have the whole object
      this._externalPreselectedInstrument = this._instruments.find(
        (instrument) => instrument.id === instrumentId,
      );
    } else {
      this._instruments = await getPaymentIntentInstruments(
        paymentIntentId,
        this._authContext?.accessToken ?? '',
      );
    }

    this.computePreSelectedInstrument();
  }

  public forceUpdateInstrumentStatus(
    startedModules: ReadonlyArray<PaymentModuleId>,
  ) {
    this._instruments = this._instruments.map((i) => {
      if (!startedModules.includes(i.moduleId)) {
        return { ...i, status: InstrumentStatus.TemporarilyUnavailable };
      }
      return i;
    });
    this.computePreSelectedInstrument();
  }

  get instruments() {
    return this._instruments;
  }

  get preSelectedInstrument() {
    return this._preSelectedInstrument;
  }

  get externalPreselectedInstrument() {
    return this._externalPreselectedInstrument;
  }

  private computePreSelectedInstrument() {
    this._preSelectedInstrument =
      this._externalPreselectedInstrument !== undefined &&
      this.externalPreselectedInstrument?.status === InstrumentStatus.Valid
        ? this._externalPreselectedInstrument
        : determineInstrumentPreSelection(
            this._rawPreSelectedPaymentMethod,
            this._instruments,
          );
  }
}
